// export const createEntitySelect = (name: string, ) =>

import * as React from 'react';

import { cn, Select } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { EntitySelectProps } from '../types';

import { QueryAtomFactoryReturn } from '~/shared/providers/api/createQueryAtomFactory';

type QueryAtomResultEntry<
  Q extends QueryAtomFactoryReturn<unknown, unknown, unknown[]>,
> = Q extends QueryAtomFactoryReturn<unknown, unknown, (infer T)[]> ? T : never;

export const createEntitySelect = <
  TData,
  T extends { id: string },
  AF extends QueryAtomFactoryReturn<unknown, TData, T[]>,
>(
  name: string,
  atomFactory: AF,
  {
    formatLabel,
    formatSubLabel,
  }: {
    formatLabel: (entity: QueryAtomResultEntry<AF>) => string;
    formatSubLabel?: (entity: QueryAtomResultEntry<AF>) => string;
  },
) => {
  const $atom = atomFactory();

  return ({
    onChange,
    showAllOption,
    allOptionLabel,
    value,
    ...props
  }: EntitySelectProps<T> & {
    showAllOption?: boolean;
    allOptionLabel?: string;
  }) => {
    const { data: entities, isLoading } = useAtomValue($atom);

    const options = React.useMemo(
      () => [
        ...(showAllOption
          ? [{ key: 'all', label: allOptionLabel ?? 'All' }]
          : []),
        ...(entities ?? []).map((it) => ({
          key: it.id,
          label: formatLabel(it as QueryAtomResultEntry<AF>),
          subLabel: formatSubLabel?.(it as QueryAtomResultEntry<AF>),
        })),
      ],
      [allOptionLabel, entities, showAllOption],
    );

    const handleChange = React.useCallback(
      (value: string | undefined) => {
        const entity = entities?.find((it) => it.id === value);
        onChange?.(value, entity as T);
      },
      [entities, onChange],
    );

    return (
      <Select
        {...props}
        value={value ?? (showAllOption ? 'all' : undefined)}
        onChange={handleChange}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={cn(`@${name}`, props.className)}
        options={options}
        isLoading={isLoading}
      />
    );
  };
};
